import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Toast} from "primereact/toast";
import {useRef, useState} from "react";

import {WebError} from "../api/web-error";
import {useRevaTranslation} from "../hooks/languageHooks";
import {type LocationPdfType, PdfOrientation} from "../redux/pdfConstants";
import {showPdfPreview} from "../redux/pdfPreviewSlice";
import {downloadPdfForType} from "../redux/pdfThunk";
import {useRevaDispatch} from "../redux/revaHooks";
import {getErrorDetail} from "../util/errorUtil";

interface Props {
    /** Karte, die als PDF erzeugt werden soll. */
    type: LocationPdfType;
    /** Text des Tooltips am Knopf. */
    tooltip: string;
    /** Ausrichtung; nur für die Karten, die beide Ausrichtungen anbieten. */
    orientation?: PdfOrientation;
    /** Abweichendes Icon; ohne Angabe Karte (quer) bzw. PDF-Symbol. */
    icon?: string;
}

export function DownloadMapPdfButton({type, tooltip, orientation, icon}: Props) {
    const dispatch = useRevaDispatch();
    const {t, language} = useRevaTranslation();
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);

    /**
     * Show PDF error toast based on error code from PDF service.
     * @param error the caught error
     */
    const showPdfError = (error: unknown) => {
        let detail = t("value.pdf.error.generic");

        if (error instanceof WebError) {
            const errorCode = error.getErrorCode();
            const errorData = error.getCustomErrorData();

            if (errorCode && t(`value.pdf.error.${errorCode}`) !== `value.pdf.error.${errorCode}`) {
                // Use translated error message for known error codes
                detail = t(`value.pdf.error.${errorCode}`);
            } else if (errorData) {
                // Use localized error detail from service response
                detail = getErrorDetail(errorData, language) || detail;
            }
        }

        toast.current?.show({
            severity: "error",
            summary: t("value.pdf.error.title"),
            detail,
            life: 5000,
        });
    };

    const onDownloadPdf = async () => {
        setLoading(true);
        try {
            await dispatch(downloadPdfForType(type, orientation));
        } catch (e) {
            console.error(e);
            showPdfError(e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex gap-2 ml-2">
            <Button
                icon={icon ?? (orientation === PdfOrientation.HORIZONTAL ? PrimeIcons.MAP : PrimeIcons.FILE_PDF)}
                tooltip={tooltip}
                tooltipOptions={{position: "left"}}
                loading={loading}
                onClick={() => void onDownloadPdf()}
                onMouseEnter={() => dispatch(showPdfPreview({type, orientation}))}
            />
            <Toast ref={toast} />
        </div>
    );
}
